'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/analytics';
import { useDialog } from '@svenskaspelab/nova-foundation/nextjs/dialog';
import { resetCoupon } from '@svenskaspelab/poolbetting/core/action/coupon';
import { useCouponId, useCouponStatus } from '@svenskaspelab/poolbetting/core/nextjs/coupon';
import { COUPON_SETTINGS_SHEET, QUICK_PICK_SHEET } from '@svenskaspelab/poolbetting/schema/input';
import { CouponToolbox, CouponToolboxButton, CouponToolboxContent } from '@svenskaspelab/poolbetting/ui/CouponToolbox';
import { TipsenLink } from '@svenskaspelab/poolbetting/ui/TipsenLink';
import Dustbin from '@svenskaspelab/ui/react/icons/Dustbin';
import Spelguide from '@svenskaspelab/ui/react/icons/Information';
import Pix from '@svenskaspelab/ui/react/icons/Pix';
import Settings from '@svenskaspelab/ui/react/icons/Settings';
import { useMediaQueryAbove } from '@svenskaspelab/ui/react/media-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@svenskaspelab/ui/react/tooltip';
import { createElement, type ReactNode, Suspense, useCallback } from 'react';
import { AddCouponsContainer, OpenSheetLink } from '@/components/index.browser';
import { useAutoTooltip, usePathname } from '@/lib/index.browser';
const ADD_COUPON_TOOLTIP_KEY = 'addCoupon';
const handleTracking = (label: string, pathname: string): void => {
  trackEvent({
    category: 'Nya tipsen',
    action: label,
    optLabel: pathname
  });
};

/**
 * Coupon toolbox for the play page.
 *
 * @returns Coupon toolbox for the play page.
 */
export default function PlayCouponToolbox() {
  const $ = _c(78);
  const [couponId, couponIndex] = useCouponId();
  const {
    showDialog
  } = useDialog();
  const isLargeScreen = useMediaQueryAbove("md");
  const pathname = usePathname();
  const {
    isOpen: isAddCouponTooltipOpen,
    isAutoShown: isAddCouponNewsShown,
    handleOpenChange: handleAddCouponTooltipOpenChange,
    ref: addCouponTooltipRef
  } = useAutoTooltip(ADD_COUPON_TOOLTIP_KEY);
  let t0;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t0 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const couponStatus = useCouponStatus(t0);
  let t1;
  if ($[3] !== couponId || $[4] !== couponIndex) {
    t1 = () => {
      (async () => {
        await resetCoupon({
          couponId,
          couponIndex
        });
      })();
    };
    $[3] = couponId;
    $[4] = couponIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resetCouponAction = t1;
  let t2;
  if ($[6] !== resetCouponAction || $[7] !== showDialog) {
    t2 = event => {
      event.preventDefault();
      (async () => {
        await showDialog({
          title: "Dina tecken kommer att rensas",
          description: createElement("p", {
            className: "text-center"
          }, "Vill du forts\xE4tta \xE4nd\xE5?"),
          buttons: {
            primary: {
              label: "Rensa",
              action: resetCouponAction
            },
            secondary: {
              label: "Avbryt"
            }
          }
        });
      })();
    };
    $[6] = resetCouponAction;
    $[7] = showDialog;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleResetCoupon = t2;
  const t3 = isLargeScreen ? "md" : "sm";
  let t4;
  if ($[9] !== pathname) {
    t4 = <OpenSheetLink sheet={QUICK_PICK_SHEET} queriesToKeep="ALL" trackingObject={{
      category: "Nya tipsen",
      action: "Fyll i med PIX!",
      optLabel: pathname
    }} />;
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Pix />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4) {
    t6 = <Suspense><CouponToolboxButton size={t3} render={t4}>{t5}Fyll i med PIX!</CouponToolboxButton></Suspense>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== pathname) {
    t7 = () => {
      handleTracking("Spelguide", pathname);
    };
    $[15] = pathname;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = isLargeScreen ? "md" : "sm";
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TipsenLink scroll={false} aria-label="Spelguide" queriesToKeep="ALL" href={{
      query: {
        modal: "gameGuide"
      }
    }} />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8) {
    t10 = <CouponToolboxButton onClick={t7} size={t8} containerClassName="lg:hidden" className="min-w-0 px-2 md:px-3 lg:min-w-18" render={t9} />;
    $[18] = t7;
    $[19] = t8;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Spelguide />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10) {
    t12 = <TooltipTrigger render={t10}>{t11}</TooltipTrigger>;
    $[22] = t10;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <TooltipContent side="top">Spelguide</TooltipContent>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12) {
    t14 = <Tooltip>{t12}{t13}</Tooltip>;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== addCouponTooltipRef) {
    t15 = <div ref={addCouponTooltipRef} />;
    $[27] = addCouponTooltipRef;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== pathname) {
    t16 = () => {
      handleTracking("L\xE4gg till kuponger", pathname);
    };
    $[29] = pathname;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = isLargeScreen ? "md" : "sm";
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <AddCouponsContainer onClick={t16} size={t17} />;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t15 || $[35] !== t18) {
    t19 = <TooltipTrigger render={t15}>{t18}</TooltipTrigger>;
    $[34] = t15;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = isAddCouponNewsShown ? "Nyhet! Spela fler kuponger samtidigt" : "L\xE4gg till fler kuponger";
  let t21;
  if ($[37] !== t20) {
    t21 = <TooltipContent side="top">{t20}</TooltipContent>;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== handleAddCouponTooltipOpenChange || $[40] !== isAddCouponTooltipOpen || $[41] !== t19 || $[42] !== t21) {
    t22 = <Tooltip open={isAddCouponTooltipOpen} onOpenChange={handleAddCouponTooltipOpenChange}>{t19}{t21}</Tooltip>;
    $[39] = handleAddCouponTooltipOpenChange;
    $[40] = isAddCouponTooltipOpen;
    $[41] = t19;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = isLargeScreen ? "md" : "sm";
  let t24;
  if ($[44] !== pathname) {
    t24 = <OpenSheetLink sheet={COUPON_SETTINGS_SHEET} aria-label={"Kuponginst\xE4llningar"} queriesToKeep="ALL" trackingObject={{
      category: "Nya tipsen",
      action: "Anpassa",
      optLabel: pathname
    }} />;
    $[44] = pathname;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t23 || $[47] !== t24) {
    t25 = <CouponToolboxButton size={t23} className="min-w-0 px-2 md:px-3 lg:min-w-18" render={t24} />;
    $[46] = t23;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Settings />;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25) {
    t27 = <TooltipTrigger render={t25}>{t26}</TooltipTrigger>;
    $[50] = t25;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <TooltipContent side="top">Inställningar</TooltipContent>;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t27) {
    t29 = <Suspense><Tooltip>{t27}{t28}</Tooltip></Suspense>;
    $[53] = t27;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  const t30 = couponStatus === "INVALID_NOT_STARTED";
  let t31;
  if ($[55] !== pathname) {
    t31 = () => {
      handleTracking("Rensa", pathname);
    };
    $[55] = pathname;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  const t32 = isLargeScreen ? "md" : "sm";
  let t33;
  if ($[57] !== t30 || $[58] !== t31 || $[59] !== t32) {
    t33 = <CouponToolboxButton className="am:ml-auto w-full min-w-0 px-2 md:px-3 lg:min-w-18" aria-label="Rensa kupong" disabled={t30} data-testid="clearCoupon" onClick={t31} type="submit" size={t32} />;
    $[57] = t30;
    $[58] = t31;
    $[59] = t32;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Dustbin />;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== t33) {
    t35 = <TooltipTrigger render={t33}>{t34}</TooltipTrigger>;
    $[62] = t33;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <TooltipContent side="top">Rensa kupong</TooltipContent>;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== t35) {
    t37 = <Tooltip>{t35}{t36}</Tooltip>;
    $[65] = t35;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== handleResetCoupon || $[68] !== t37) {
    t38 = <form onSubmit={handleResetCoupon}>{t37}</form>;
    $[67] = handleResetCoupon;
    $[68] = t37;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== t14 || $[71] !== t22 || $[72] !== t29 || $[73] !== t38) {
    t39 = <TooltipProvider><div className="flex gap-2">{t14}{t22}{t29}{t38}</div></TooltipProvider>;
    $[70] = t14;
    $[71] = t22;
    $[72] = t29;
    $[73] = t38;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t39 || $[76] !== t6) {
    t40 = <CouponToolbox><CouponToolboxContent className="bg-surface justify-between pt-0">{t6}{t39}</CouponToolboxContent></CouponToolbox>;
    $[75] = t39;
    $[76] = t6;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  return t40;
}